/**
 * ShipmentCard — list item showing the same compact info as Flutter
 * `my_shipments_page.dart` cards: tracking + status + route + receiver
 * + weight + price.
 */
import { Link } from 'react-router-dom';
import { Hash, MapPin, Weight, ArrowLeft, User } from 'lucide-react';
import type { Shipment } from '@/types/shipment';
import { StatusBadge } from './StatusBadge';

export function ShipmentCard({ shipment }: { shipment: Shipment }) {
  const from = shipment.pickup_city || shipment.origin_city || '—';
  const to = shipment.receiver_city || shipment.destination_city || '—';
  const price = shipment.final_price ?? shipment.price ?? shipment.estimated_price;

  return (
    <Link
      to={`/shipments/${shipment.id}`}
      className="block bg-surface border border-border rounded-xl p-4 hover:border-primary hover:shadow-sm transition"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 font-mono text-sm text-primary">
          <Hash size={14} />
          {shipment.tracking_number}
        </div>
        <StatusBadge status={shipment.status} />
      </div>

      <div className="flex items-center gap-2 text-text-secondary text-sm mb-2">
        <MapPin size={14} className="text-primary" />
        <span>{from}</span>
        <ArrowLeft size={14} />
        <span className="font-semibold text-text-primary">{to}</span>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1.5 text-text-secondary">
          <User size={13} />
          <span>{shipment.receiver_name}</span>
        </div>
        <div className="flex items-center gap-3 text-text-secondary">
          <span className="flex items-center gap-1">
            <Weight size={13} /> {shipment.weight} كغ
          </span>
          {price != null && (
            <span className="font-semibold text-secondary">
              {price} {shipment.currency || 'USD'}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
